import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Modal,
  Typography,
  Paper,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import DoneOutlineOutlinedIcon from "@mui/icons-material/DoneOutlineOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import PageButtonWhite from "./PageButtonWhite";
import PageButtonBlack from "./PageButtonBlack";
import PageButtonBlue from "./PageButtonBlue";
import * as XLSX from "xlsx";
import {
  ETIQUETA_TIPO_PLURAL,
  ImpactoDocumentos,
  TablaDocumentosImpacto,
  accionDocumentos,
  decisionesIniciales,
  detalleError,
  ejecutarCambioTipo,
  listaDecisiones,
  obtenerImpacto,
  regularizarDocumentos,
  textosCambio,
} from "../Representantes/ImpactoDocumentosRepresentante";

const DESCRIPCION_REHABILITAR = "Esta acción permitirá a los usuarios entrar a la plataforma como representantes del establecimiento, pero puede ser revertido si usted Rol MINEDUC vuelve a cambiar el Tipo de Representante.";

export default function ModalMassiveRepresentantes(props) {
  const { open, onClose, selected = [], representantes, action } = props;
  const [loading, setLoading]     = useState(false);
  const [results, setResults]     = useState([]);
  const [processed, setProcessed] = useState(0);
  const [started, setStarted]     = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [decisiones, setDecisiones] = useState({});
  const [cargandoImpacto, setCargandoImpacto] = useState(false);
  const [errorImpacto, setErrorImpacto] = useState("");
  const [docResults, setDocResults] = useState([]);
  const [errorRegularizacion, setErrorRegularizacion] = useState("");

  const objetivos = useMemo(() => {
    if (representantes && representantes.length) {
      return representantes.map((r) => ({ id: r.key, rbd: r.rbd, run: r.run, origen: r.tipo_representante }));
    }
    return selected.map((combined) => {
      const [rbd, run] = combined.split("_");
      return { id: combined, rbd, run, origen: "Temporal" };
    });
  }, [representantes, selected]);

  const origenes = useMemo(() => [...new Set(objetivos.map((o) => o.origen))], [objetivos]);
  const origenComun = origenes.length === 1 ? origenes[0] : null;
  const accion = accionDocumentos(origenComun || "Temporal", action);

  const successfulCount = results.filter((r) => r.status === "Exitoso").length;
  const failedCount     = results.filter((r) => r.status === "Fallido").length;
  const docFailedCount  = docResults.filter((r) => r.status === "Fallido").length;

  useEffect(() => {
    if (!open) return;
    setResults([]);
    setProcessed(0);
    setLoading(false);
    setStarted(false);
    setDocumentos([]);
    setDecisiones({});
    setErrorImpacto("");
    setDocResults([]);
    setErrorRegularizacion("");
    if (!accion || !objetivos.length) return;

    let vigente = true;
    setCargandoImpacto(true);
    obtenerImpacto(accion, objetivos.map((o) => ({ rut: o.run, rbd: o.rbd })))
      .then((docs) => {
        if (!vigente) return;
        setDocumentos(docs);
        setDecisiones(decisionesIniciales(docs));
      })
      .catch((err) => vigente && setErrorImpacto(detalleError(err, "Error en petición")))
      .finally(() => vigente && setCargandoImpacto(false));
    return () => {
      vigente = false;
    };
  }, [open, objetivos, accion]);

  const callActions = useCallback(async () => {
    if (!objetivos.length) return;

    setStarted(true);
    setLoading(true);

    const allResults = [];
    for (const objetivo of objetivos) {
      const { id, rbd, run, origen } = objetivo;
      try {
        await ejecutarCambioTipo(origen, action, run, rbd);
        allResults.push({ id, rbd, run, status: "Exitoso", detail: "" });
      } catch (err) {
        allResults.push({ id, rbd, run, status: "Fallido", detail: detalleError(err, "Error en petición") });
      }
      setResults([...allResults]);
      setProcessed(allResults.length);
    }

    const exitosos = allResults.filter((r) => r.status === "Exitoso");
    if (accion && exitosos.length) {
      try {
        const resultados = await regularizarDocumentos(
          accion,
          exitosos.map((r) => ({ rut: r.run, rbd: r.rbd })),
          listaDecisiones(documentos, decisiones)
        );
        setDocResults(resultados);
      } catch (err) {
        setErrorRegularizacion(detalleError(err, "Error en petición"));
      }
    }

    setLoading(false);
  }, [action, accion, objetivos, documentos, decisiones]);

  const downloadXLS = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(results);
    XLSX.utils.book_append_sheet(wb, ws, "Resultados");
    if (docResults.length) {
      const wsDocs = XLSX.utils.json_to_sheet(
        docResults.map((d) => ({
          tipo: d.tipo,
          rbd: d.rbd,
          folio: d.folio,
          operador: d.operador,
          estado_previo: d.estado,
          accion_sugerida: d.accion_sugerida,
          resultado: d.resultado,
          status: d.status,
          detalle: d.detail,
        }))
      );
      XLSX.utils.book_append_sheet(wb, wsDocs, "Documentos");
    }
    XLSX.writeFile(wb, "resultados.xlsx");
    onClose(true, successfulCount);
  };

  if (!open) return null;

  const textos = textosCambio(action, true);
  const descripcion = origenComun === "Deshabilitado" ? DESCRIPCION_REHABILITAR : textos.descripcion;
  const etiquetaSeleccion = origenComun ? ETIQUETA_TIPO_PLURAL[origenComun] || "Representantes" : "Representantes";

  return (
    <Modal
      open={open}
      onClose={() => !loading && onClose(true, successfulCount)}
      aria-labelledby="modal-massive-title"
      sx={{
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      }}
      slotProps={{ backdrop: { sx: { opacity: "0.1!important" } } }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "800px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: 0,
          overflowY: "auto",
          borderRadius: "16px",
        }}
      >
        <Paper elevation={3} sx={{ p: 3, textAlign: "center", position: "relative", borderRadius: "16px" }}>
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={() => onClose(true, successfulCount)}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>

          {!started ? (
            <Box sx={{ mb: 2 }}>
              <GroupOutlinedIcon color="error" sx={{ fontSize: 45 }} />
              <Typography
                id="modal-massive-title"
                variant="h2"
                component="h1"
                fontWeight="bold"
                gutterBottom
                sx={{ mb: 1, color: "#4D4D4D" }}
              >
                Acción en masa (Pasar a {action})
              </Typography>
              <Typography sx={{ color: "#111", mb: 1 }}>
                <b>Usted seleccionó {objetivos.length}</b> {etiquetaSeleccion}
              </Typography>

              {accion && (
                <ImpactoDocumentos
                  cargando={cargandoImpacto}
                  error={errorImpacto}
                  documentos={documentos}
                  decisiones={decisiones}
                  onDecision={(clave, valor) => setDecisiones((prev) => ({ ...prev, [clave]: valor }))}
                  masivo
                />
              )}

              <Typography sx={{ color: "error.main", fontWeight: 700, fontSize: "1.125rem", mt: 2 }}>
                {textos.pregunta}
              </Typography>
              <Box sx={{ maxWidth: "600px", margin: "0 auto" }}>
                <Typography variant="body2" sx={{ color: "error.main" }}>
                  {descripcion}
                </Typography>
              </Box>

              <Box mt={3} display="flex" justifyContent="center" sx={{ gap: 2 }}>
                <PageButtonWhite onClick={() => onClose(true, 0)} width="150px">
                  Cancelar
                </PageButtonWhite>
                <PageButtonBlack onClick={callActions} disabled={loading || cargandoImpacto} width="320px">
                  {textos.boton}
                </PageButtonBlack>
              </Box>
            </Box>
          ) : (
            <>
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{
                  fontSize: "26px",
                  my: 1,
                  color: "#4D4D4D",
                  textTransform: "uppercase",
                }}
              >
                Resultados ({processed}/{objetivos.length})
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 4, my: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <DoneOutlineOutlinedIcon color="success" />
                  <Typography variant="body1" fontWeight="bold">
                    {successfulCount} Exitosos
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ErrorOutlineIcon color="error" />
                  <Typography variant="body1" fontWeight="bold">
                    {failedCount} Fallidos
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ my: 1 }}>
                <Typography variant="body1">
                  Procesados: {processed} / {objetivos.length}
                </Typography>
                {loading && <LinearProgress sx={{ mt: 1 }} />}
              </Box>

              <TableContainer
                sx={{
                  p: 1,
                  maxHeight: "300px",
                  overflow: "auto",
                  mb: 2,
                  "&::-webkit-scrollbar": { width: "8px" },
                  "&::-webkit-scrollbar-thumb": { backgroundColor: "#888", borderRadius: 4 },
                  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
                }}
              >
                <Table stickyHeader sx={{ borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">RBD</TableCell>
                      <TableCell align="center">RUN</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Detalle</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell align="center">{r.rbd}</TableCell>
                        <TableCell align="center">{r.run}</TableCell>
                        <TableCell align="center">
                          {r.status === "Exitoso" ? (
                            <>
                              <DoneOutlineOutlinedIcon color="success" /> Exitoso
                            </>
                          ) : (
                            <>
                              <ErrorOutlineIcon color="error" /> {r.status}
                            </>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {r.status === "Exitoso"
                            ? "Acción completada"
                            : (r.detail || "-")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {!loading && errorRegularizacion && (
                <Typography sx={{ color: "error.main", mb: 2 }}>
                  Los Representantes fueron actualizados, pero no fue posible regularizar sus documentos: {errorRegularizacion}
                </Typography>
              )}

              {!loading && docResults.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: "#111" }}>
                    Documentos regularizados: {docResults.length - docFailedCount} de {docResults.length}
                  </Typography>
                  <TablaDocumentosImpacto documentos={docResults} mostrarRbd mostrarDetalle />
                </Box>
              )}

              <Box sx={{ mb: 2, pt: 1 }}>
                {!loading ? (
                  <PageButtonBlue width="350px" onClick={downloadXLS}>
                    Cerrar y descargar resumen
                  </PageButtonBlue>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Procesando… por favor espera
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Modal>
  );
}
